import React, { useState, useEffect } from "react";
import {
  Slideout,
  Typography,
  Button,
  Icon,
  Tag,
  Switch,
  Scrollbar,
} from "@ann/web-components";
import {
  SlideoutContainer,
  MainContentWrapper,
  ScrollableContentWrapper,
  Breadcrumb,
  SummarySection,
  TopicSection,
  MetadataSection,
  SectionHeader,
  SectionBody,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  TopicSectionHeader,
  TopicItem,
  TopicContent,
  SwitchWrapper,
  ScrollableContent,
  SlideoutActions,
  NavigationBar,
} from "./SlideoutDocumentDetail.styled";
import { useStore } from "../../../../../../../hooks";
import { observer } from "mobx-react";

const SlideoutDocumentDetail = observer(({ open, onClose, documentData }) => {
  const { knowledgeStore } = useStore();

  const [loading, setLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (!open || !documentData?.uuid) {
      return;
    }

    const fetchDocumentDetails = async () => {
      setLoading(true);


      const mockDetails = {
        uuid: documentData.uuid,
        title: documentData.title || "Doc 1",
        driveName: "Drive Name 1",
        summary:
          "This internal project proposal outlines the launch strategy for our Q3 product update. It includes audience segmentation, resource allocation, and a detailed timeline covering pre-launch, rollout, and post-launch phases. Key metrics for success have been defined, and department-specific contributions are clearly outlined. Review is recommended before September 1st to ensure alignment with cross-functional teams.",
        lastUpdated: "08/04/2025",
        audience: "Internal",
        status: "Active",
        owner: "Thomas",
        version: "1.1.0",
        topics: [
          {
            id: "product-strategy",
            name: "Product Strategy",
            description:
              "This internal project proposal outlines the launch strategy for our Q3 product update.",
            enabled: true,
          },
          {
            id: "metrics",
            name: "Metrics",
            description:
              "This internal project proposal outlines the launch strategy for our Q3 product update.",
            enabled: false,
          },
          {
            id: "segmentation",
            name: "Segmentation",
            description:
              "This internal project proposal outlines the launch strategy for our Q3 product update.",
            enabled: true,
          },
          {
            id: "product-update",
            name: "Product Update",
            description:
              "This internal project proposal outlines the launch strategy for our Q3 product update.",
            enabled: true,
          },
          {
            id: "timeline",
            name: "Timeline",
            description:
              "This internal project proposal outlines the launch strategy for our Q3 product update.",
            enabled: true,
          },
        ],
      };

      setDocumentDetails(mockDetails);
      setTopics(mockDetails.topics);
      setSelectAll(mockDetails.topics.every((topic) => topic.enabled));
      setLoading(false);
    };

    fetchDocumentDetails();
  }, [open, documentData]);

  const handleGoToDocument = () => {
    console.log("Navigate to document:", documentDetails?.uuid);
  };

  const handleSelectAllToggle = (value) => {
    setSelectAll(value);
    setTopics(topics.map((topic) => ({ ...topic, enabled: value })));
  };

  const handleTopicToggle = (topicId, value) => {
    const updatedTopics = topics.map((topic) =>
      topic.id === topicId ? { ...topic, enabled: value } : topic
    );
    setTopics(updatedTopics);
    setSelectAll(updatedTopics.every((topic) => topic.enabled));
  };

  if (!documentDetails && !loading) {
    return null;
  }

  return (
    <Slideout onClose={onClose} size="medium" open={open}>
      <Slideout.HeaderActions>
        <Icon name="arrowLeft" size="large" onClick={onClose} />
      </Slideout.HeaderActions>

      <SlideoutContainer>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <Typography category="body" size="medium">
              Loading document details...
            </Typography>
          </div>
        ) : (
          documentDetails && (
            <>
              <Typography category="title" size="large" weight="bold">
                {documentDetails.title}
              </Typography>

              <MainContentWrapper>
                <NavigationBar>
                  <Button
                    iconLeft="arrowLeft"
                    label="Back"
                    size="small"
                    type="tertiary"
                  />
                  <Breadcrumb>
                    <Typography
                      category="body"
                      size="small"
                      weight="regular"
                      color="subtitle"
                    >
                      Drive / {documentDetails.driveName} /{" "}
                      {documentDetails.title}
                    </Typography>
                  </Breadcrumb>
                </NavigationBar>

                <ScrollableContentWrapper>
                  <Scrollbar size="small">
                    <ScrollableContent>
                      <SummarySection>
                        <SectionHeader>
                          <Icon color="primary-alt" name="aiChat" />
                          <Typography
                            category="subtitle"
                            size="medium"
                            weight="bold"
                          >
                            Summary
                          </Typography>
                        </SectionHeader>
                        <SectionBody>
                          <Typography
                            category="body"
                            size="medium"
                            weight="regular"
                            color="body"
                          >
                            {documentDetails.summary}
                          </Typography>
                        </SectionBody>
                      </SummarySection>

                      <MetadataSection>
                        <MetadataItem>
                          <MetadataLabel>
                            <Typography
                              category="subtitle"
                              size="medium"
                              weight="bold"
                              color="subtitle"
                            >
                              Last Updated
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography
                              category="body"
                              size="small"
                              weight="regular"
                              color="body"
                            >
                              {documentDetails.lastUpdated}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography
                              category="subtitle"
                              size="medium"
                              weight="bold"
                              color="subtitle"
                            >
                              Audience
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography
                              category="body"
                              size="small"
                              weight="regular"
                              color="body"
                            >
                              {documentDetails.audience}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography
                              category="subtitle"
                              size="medium"
                              weight="bold"
                              color="subtitle"
                            >
                              Status
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Tag
                              color="informative"
                              onClose={() => {}}
                              size="medium"
                              text={documentDetails.status}
                            />
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography
                              category="subtitle"
                              size="medium"
                              weight="bold"
                              color="subtitle"
                            >
                              Owner
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography
                              category="body"
                              size="small"
                              weight="regular"
                              color="body"
                            >
                              {documentDetails.owner}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>

                        <MetadataItem>
                          <MetadataLabel>
                            <Typography
                              category="subtitle"
                              size="medium"
                              weight="bold"
                              color="subtitle"
                            >
                              Version
                            </Typography>
                          </MetadataLabel>
                          <MetadataValue>
                            <Typography
                              category="body"
                              size="small"
                              weight="regular"
                              color="body"
                            >
                              {documentDetails.version}
                            </Typography>
                          </MetadataValue>
                        </MetadataItem>
                      </MetadataSection>

                      <TopicSection>
                        <TopicSectionHeader>
                          <Typography
                            category="subtitle"
                            size="medium"
                            weight="bold"
                            color="subtitle"
                          >
                            Topics
                          </Typography>
                          <Typography
                            category="body"
                            size="medium"
                            weight="regular"
                            color="body"
                          >
                            Select the topics you want your agent to access
                          </Typography>
                        </TopicSectionHeader>

                        <TopicItem>
                          <SwitchWrapper>
                            <Switch
                              defaultChecked={selectAll}
                              onChange={handleSelectAllToggle}
                              size="medium"
                            />
                          </SwitchWrapper>
                          <TopicContent>
                            <Typography
                              category="body"
                              size="medium"
                              weight="bold"
                            >
                              Select All Topics
                            </Typography>
                          </TopicContent>
                        </TopicItem>

                        {topics.map((topic) => (
                          <TopicItem key={topic.id}>
                            <SwitchWrapper>
                              <Switch
                                defaultChecked={topic.enabled}
                                onChange={(value) =>
                                  handleTopicToggle(topic.id, value)
                                }
                                size="medium"
                              />
                            </SwitchWrapper>
                            <TopicContent>
                              <Typography
                                category="subtitle"
                                size="small"
                                weight="bold"
                                color="subtitle"
                              >
                                {topic.name}
                              </Typography>
                              <Typography
                                category="body"
                                size="medium"
                                weight="regular"
                                color="body"
                              >
                                {topic.description}
                              </Typography>
                            </TopicContent>
                          </TopicItem>
                        ))}
                      </TopicSection>
                    </ScrollableContent>
                  </Scrollbar>
                </ScrollableContentWrapper>

                <SlideoutActions>
                  <Button
                    label="Go to document"
                    size="medium"
                    type="primary"
                    onClick={handleGoToDocument}
                  />
                </SlideoutActions>
              </MainContentWrapper>
            </>
          )
        )}
      </SlideoutContainer>
    </Slideout>
  );
});

export default SlideoutDocumentDetail;
